import React from "react";
import "./searchable-dropdown.css";
import { AngleDown, SearchIconV1 } from "images/image-component";
import { SearchableDropdownProps } from "./types";
import { useSearchableDropdown } from "./useSearchableDropdown";

/**
 * A themed, accessible dropdown with an inline search box. Presentation only —
 * all behaviour lives in {@link useSearchableDropdown}. Generic over the value
 * type so it can back any dataset (defaults to `number` for coded lists such as
 * ISO country codes and MCCs).
 */
function SearchableDropdown<T extends string | number = number>({
  options,
  value,
  onChange,
  name,
  label,
  placeholder = "Select an option",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches",
  disabled = false,
  maxRendered = 100,
}: SearchableDropdownProps<T>) {
  const {
    open,
    query,
    highlighted,
    filtered,
    visible,
    truncatedCount,
    selected,
    containerRef,
    triggerRef,
    searchRef,
    listRef,
    toggle,
    select,
    setHighlighted,
    onTriggerKeyDown,
    onListKeyDown,
    onSearchChange,
  } = useSearchableDropdown<T>({ options, value, onChange, disabled, maxRendered });

  const listId = `${name}-listbox`;
  const optionId = (i: number) => `${name}-opt-${i}`;

  // Always name the trigger by its field first, then the current value, so the
  // field's purpose is announced even after a value is chosen.
  const triggerLabel = label
    ? selected
      ? `${label}: ${selected.label}`
      : label
    : undefined;

  const resultSummary =
    filtered.length === 0 ? emptyMessage : `${filtered.length} results`;

  return (
    <div className="v1-field">
      {label && (
        <label className="v1-field-label" htmlFor={name}>
          {label}
        </label>
      )}

      <div className="sd-root" ref={containerRef}>
        <button
          type="button"
          id={name}
          ref={triggerRef}
          className={`sd-trigger${open ? " sd-open" : ""}`}
          onClick={toggle}
          onKeyDown={onTriggerKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={triggerLabel}
        >
          <span className={`sd-value${selected ? "" : " sd-placeholder"}`}>
            {selected ? (
              <>
                {selected.code && <span className="sd-code">{selected.code}</span>}
                <span className="sd-label">{selected.label}</span>
              </>
            ) : (
              placeholder
            )}
          </span>
          <AngleDown size={18} />
        </button>

        {open && (
          <div className="sd-menu">
            <div className="sd-search">
              <SearchIconV1 size={16} />
              <input
                ref={searchRef}
                type="text"
                role="combobox"
                className="sd-search-input"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={onListKeyDown}
                aria-expanded={open}
                aria-controls={listId}
                aria-activedescendant={
                  visible[highlighted] ? optionId(highlighted) : undefined
                }
                aria-label={label ? `Search ${label}` : "Search options"}
                autoComplete="off"
              />
            </div>

            <div className="sd-sr-only" role="status" aria-live="polite">
              {resultSummary}
            </div>

            {filtered.length === 0 ? (
              <div className="sd-empty" aria-hidden="true">
                {emptyMessage}
              </div>
            ) : (
              <ul
                className="sd-list"
                ref={listRef}
                id={listId}
                role="listbox"
                aria-label={label}
              >
                {visible.map((opt, i) => {
                  const isSelected = opt.value === value;
                  const isHighlighted = i === highlighted;
                  return (
                    <li
                      key={String(opt.value)}
                      id={optionId(i)}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={opt.disabled || undefined}
                      className={`sd-option${isHighlighted ? " sd-hi" : ""}${
                        isSelected ? " sd-selected" : ""
                      }${opt.disabled ? " sd-disabled" : ""}`}
                      onMouseEnter={() => !opt.disabled && setHighlighted(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => select(opt)}
                    >
                      {opt.code && <span className="sd-code">{opt.code}</span>}
                      <span className="sd-label">{opt.label}</span>
                      {isSelected && (
                        <span className="sd-check" aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </li>
                  );
                })}
                {truncatedCount > 0 && (
                  <li className="sd-hint" aria-hidden="true">
                    {truncatedCount} more — refine your search
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchableDropdown;
